import { mapGetters, mapActions, mapMutations } from 'vuex'
import countries from '@/utils/_countries.json'
import languages from '@/utils/_languages.json'

const DEFAULT_STEP_ROUTE = {
  1: 'CreateContentplan',
  2: 'CreateContentplanStep3',
  3: 'CreateContentplanStep4'
}

const SHOPIFY_STEP_ROUTE = {
  1: 'CreateContentplan',
  2: 'CreateContentplanShopify'
}

const ROUTE_STEP = {
  CreateContentplan: 1,
  CreateContentplanStep3: 2,
  CreateContentplanShopify: 2,
  CreateContentplanStep4: 3
}

export default {
  name: 'CreateContentplan',
  data () {
    return {
      step: 1,
      isLoading: {
        page: false,
        contentplan: false,
        saving: false,
        domains: false,
        connectGsc: false,
        connectWordpress: false,
        connectShopify: false,
        shopifyBlogs: false
      },
      name: null,
      country: null,
      language: null,
      gscProperty: null,
      domains: null,
      wordpressApiKey: '',
      wordpressIntegrationSuccess: false,
      formValid: false,
      gscState: {
        noToken: false,
        noAccess: false,
        noDomains: false
      },
      errorState: {
        hasError: false,
        message: ''
      },
      isShopifyInstallMode: false,
      shopifyPendingShop: null,
      shopifyBlogs: [],
      selectedShopifyBlogId: null,
      shopifyBlogsLoaded: false,
      countries,
      languages,
      wordpressApiKeyRules: [
        v => !!(v && v.trim()) || 'API key is required'
      ],
      urlRules: [
        v => !!v || 'Website is required',
        v => /^[^ "]+\.[^ "]{2,}$/.test(v?.replace(/^(http|https):\/\//, '')) || 'Please enter a valid domain (e.g., example.com)'
      ]
    }
  },
  computed: {
    ...mapGetters([
      'wordpressIntegration',
      'shopifyIntegration'
    ]),
    workspaceId () {
      return Number(this.$route.params.workspaceId)
    },
    contentplanId () {
      const id = this.$route.params.contentplanId
      return id ? Number(id) : null
    },
    currentStepFromRoute () {
      return ROUTE_STEP[this.$route.name] || 1
    },
    isFormValid () {
      return this.formValid && !!this.name && !!this.country && !!this.language
    },
    gscConnected () {
      if (this.isLoading.domains) return false
      if (this.gscState.noToken || this.gscState.noAccess) return false
      return Array.isArray(this.domains) && this.domains.length > 0
    },
    wordpressConnected () {
      if (!this.wordpressIntegration) return false
      if (this.wordpressIntegration.type === 'wp-plugin') return true
      return !!this.wordpressIntegration.wp_domain
    },
    shopifyConnected () {
      return !!this.shopifyIntegration?.shopify_store && !this.shopifyIntegration?.shopify_oauth_state
    },
    connectedShopifyStore () {
      return this.shopifyIntegration?.shopify_store || this.shopifyPendingShop || ''
    },
    shopifyBlogSaved () {
      return !!this.shopifyIntegration?.shopify_blog_id
    },
    isDefaultOnboarding () {
      return !this.isShopifyInstallMode
    },
    wordpressPluginInstallUrl () {
      const websiteUrl = this.normalizeWebsiteUrl(this.name)
      if (!websiteUrl) return ''

      const origin = new URL(websiteUrl).origin
      const params = new URLSearchParams({
        s: 'ContentGecko Connector',
        tab: 'search',
        type: 'term'
      })

      return `${origin}/wp-admin/plugin-install.php?${params.toString()}`
    },
    redirectUri () {
      return `${window.location.origin}/shopify-auth-callback`
    }
  },
  watch: {
    '$route.name': 'onRouteNameChange',
    '$route.params.contentplanId': 'onContentplanChange'
  },
  async mounted () {
    await this.bootstrap()
  },
  methods: {
    ...mapActions([
      'contentplanPost',
      'contentplanGetList',
      'postSearchConsoleIntegration',
      'getSearchConsoleProperties',
      'updateWorkspace',
      'pagePost',
      'getIntegrations',
      'postWordpressIntegration',
      'contentplanGetOne',
      'initiateShopifyAuth',
      'resolveShopifyStoreDomain',
      'getShopifyBlogs',
      'saveShopifyIntegration'
    ]),
    ...mapMutations([
      'setSnackbar',
      'setAuthToken',
      'updateContentplans'
    ]),
    async bootstrap () {
      // Check if we received a new auth token after OAuth flow
      const token = this.$route.query.token
      if (token) {
        console.log('Received new auth token after OAuth, updating session')
        this.setAuthToken(token)
        // Remove token from URL without page reload
        this.$router.replace({
          query: { ...this.$route.query, token: undefined }
        }).catch(() => {})
      }

      const pendingShop = sessionStorage.getItem('shopify_pending_shop')
      if (pendingShop) {
        this.isShopifyInstallMode = true
        this.shopifyPendingShop = pendingShop
      } else {
        this.isShopifyInstallMode = false
        this.shopifyPendingShop = null
      }

      this.isLoading.page = true
      this.clearError()
      this.syncStepWithRoute()

      if (!this.contentplanId) {
        this.resetContentplanState()
        if (this.isShopifyInstallMode) await this.prefillShopifyInstallDomain()
        this.isLoading.page = false
        return
      }

      try {
        await this.fetchContentplan()
        await this.refreshIntegrations()
        await this.handleStepSideEffects(this.step)
      } catch (error) {
        this.handleFatal(error, 'Failed to load onboarding data. Please refresh and try again.')
      } finally {
        this.isLoading.page = false
      }
    },
    async onRouteNameChange () {
      const previousStep = this.step
      this.syncStepWithRoute()
      if (!this.contentplanId) {
        if (this.isShopifyInstallMode && this.step === 1) await this.prefillShopifyInstallDomain()
        return
      }
      if (this.step !== previousStep) {
        await this.handleStepSideEffects(this.step)
      }
    },
    async onContentplanChange (nextId, previousId) {
      if (nextId === previousId) return
      if (!nextId) {
        this.resetContentplanState()
        this.syncStepWithRoute()
        return
      }
      await this.bootstrap()
    },
    syncStepWithRoute () {
      const target = this.currentStepFromRoute
      if (this.step !== target) this.step = target
    },
    resetContentplanState () {
      this.domains = null
      this.gscProperty = null
      this.gscState = { noToken: false, noAccess: false, noDomains: false }
      this.wordpressApiKey = ''
      this.wordpressIntegrationSuccess = false
      this.shopifyBlogs = []
      this.selectedShopifyBlogId = null
      this.shopifyBlogsLoaded = false
    },
    upsertContentplanInState ({ contentplanId, websiteUrl, planName }) {
      if (!contentplanId) return
      const currentContentplans = Array.isArray(this.$store.state.contentplans) ? this.$store.state.contentplans : []
      if (currentContentplans.some(item => item.id === contentplanId)) return
      const name = planName || websiteUrl
      this.updateContentplans([
        ...currentContentplans,
        {
          id: contentplanId,
          name,
          domain: websiteUrl?.replace(/^(https?:\/\/)/, ''),
          country: this.country,
          language: this.language
        }
      ])
    },
    async fetchContentplan () {
      await this.withLoader('contentplan', async () => {
        try {
          const contentplan = await this.contentplanGetOne({ contentplanId: this.contentplanId })
          this.name = contentplan.name?.replace(/^(https?:\/\/)/, '') || ''
          this.country = contentplan.country || null
          this.language = contentplan.language || null
        } catch (error) {
          throw this.enrichError(error, 'Failed to load content plan details')
        }
      })
    },
    async handleStepSideEffects (step) {
      if (!this.contentplanId) return
      if (this.isShopifyInstallMode && step === 2) {
        await this.ensureShopifyInstallConnected()
        return
      }
      if (this.isDefaultOnboarding && step === 2) await this.ensureGscData()
      if (this.isDefaultOnboarding && step === 3) await this.refreshIntegrations()
    },
    async saveContentplan () {
      if (!this.$refs.form?.validate()) return

      const websiteUrl = this.normalizeWebsiteUrl(this.name)
      if (!websiteUrl) {
        this.setSnackbar({ open: true, text: 'Please enter a valid domain (e.g., example.com).', type: 'warning' })
        return
      }
      const contentplanName = websiteUrl
      try {
        await this.withLoader('saving', async () => {
          const contentplan = await this.contentplanPost({
            workspaceId: this.workspaceId,
            name: contentplanName,
            country: this.country,
            language: this.language,
            type: 'managed'
          })
          this.upsertContentplanInState({
            contentplanId: contentplan.contentplanId,
            websiteUrl,
            planName: contentplanName
          })

          try {
            await this.pagePost({
              contentplanId: contentplan.contentplanId,
              urls: [websiteUrl]
            })
          } catch (error) {
            console.error('Failed to add initial page', error)
            this.setSnackbar({
              open: true,
              text: 'Content plan created but failed to add the start page. You can add it later in the dashboard.',
              type: 'warning'
            })
          }

          try {
            await this.contentplanGetList({ workspaceId: this.workspaceId })
          } catch (error) {
            console.error('Failed to refresh content plans after creation', error)
          }

          if (this.isShopifyInstallMode) {
            await this.startShopifyAuthInBackground(contentplan.contentplanId)
            return
          }

          this.navigateToStep(2, contentplan.contentplanId)
        })
      } catch (error) {
        console.error('Failed to save content plan', error)
        const message = this.getErrorMessage(error, 'Failed to create content plan. Please check your information and try again.')
        this.setSnackbar({ open: true, text: message, type: 'error' })
      }
    },
    async prefillShopifyInstallDomain () {
      if (!this.isShopifyInstallMode || !this.shopifyPendingShop || this.name) return

      try {
        const response = await this.resolveShopifyStoreDomain({
          shopifyStore: this.shopifyPendingShop
        })
        const publicDomain = response?.publicDomain
        if (publicDomain) {
          this.name = publicDomain
          return
        }
      } catch (error) {
        console.error('Failed to resolve Shopify public domain', error)
      }

      this.name = this.shopifyPendingShop
    },
    async ensureShopifyInstallConnected () {
      if (!this.contentplanId || !this.shopifyPendingShop) return

      await this.refreshIntegrations()

      if (!this.shopifyConnected) {
        await this.startShopifyAuthInBackground()
        return
      }

      this.selectedShopifyBlogId = this.shopifyIntegration?.shopify_blog_id || this.selectedShopifyBlogId
      await this.fetchShopifyInstallBlogs()
    },
    async startShopifyAuthInBackground (contentplanId = null) {
      const targetContentplanId = contentplanId || this.contentplanId
      if (!targetContentplanId || !this.shopifyPendingShop || this.isLoading.connectShopify) return
      this.isLoading.connectShopify = true

      try {
        sessionStorage.setItem('shopify_store', this.shopifyPendingShop)
        sessionStorage.setItem('shopify_contentplan_id', String(targetContentplanId))
        sessionStorage.setItem('active_workspace_id', String(this.workspaceId))
        sessionStorage.setItem('shopify_install_onboarding', 'true')

        const response = await this.initiateShopifyAuth({
          shopifyStore: this.shopifyPendingShop,
          contentplanId: targetContentplanId,
          redirectUri: this.redirectUri
        })
        if (!response?.authUrl) throw new Error('No authorization URL returned from server')

        window.location.href = response.authUrl
      } catch (error) {
        console.error('Error initiating Shopify OAuth:', error)
        this.setSnackbar({
          open: true,
          text: `Error: ${error?.response?.data?.message || error.message || 'Could not connect to Shopify'}`,
          type: 'error'
        })
        this.isLoading.connectShopify = false
      }
    },
    async fetchShopifyInstallBlogs ({ forceRefresh = false } = {}) {
      if (!this.contentplanId) return
      if (this.isLoading.shopifyBlogs) return
      if (this.shopifyBlogsLoaded && !forceRefresh) return

      this.isLoading.shopifyBlogs = true
      try {
        const response = await this.getShopifyBlogs({ contentplanId: this.contentplanId })
        this.shopifyBlogs = Array.isArray(response?.blogs) ? response.blogs : []
        this.shopifyBlogsLoaded = true
      } catch (error) {
        console.error('Error fetching Shopify blogs:', error)
        this.setSnackbar({
          open: true,
          text: `Error fetching Shopify blogs: ${error?.response?.data?.message || error.message || 'Unknown error'}`,
          type: 'error'
        })
      } finally {
        this.isLoading.shopifyBlogs = false
      }
    },
    async saveShopifyBlogSelection () {
      if (!this.contentplanId || !this.selectedShopifyBlogId || !this.connectedShopifyStore) {
        this.setSnackbar({ open: true, text: 'Please select a Shopify blog first.', type: 'warning' })
        return
      }

      try {
        await this.withLoader('saving', async () => {
          await this.saveShopifyIntegration({
            contentplanId: this.contentplanId,
            type: 'shopify',
            shopifyStore: this.connectedShopifyStore,
            shopifyBlogId: this.selectedShopifyBlogId
          })
          await this.refreshIntegrations()
          this.setSnackbar({ open: true, text: 'Shopify blog saved.', type: 'success' })
        })
      } catch (error) {
        const message = this.getErrorMessage(error, 'Could not save Shopify blog selection.')
        this.setSnackbar({ open: true, text: message, type: 'error' })
      }
    },
    async ensureGscData () {
      if (this.domains !== null) return

      this.isLoading.domains = true
      this.gscState = { noToken: false, noAccess: false, noDomains: false }

      try {
        const properties = await this.getSearchConsoleProperties()
        this.domains = properties || []
        if (!this.domains.length) {
          this.gscState.noDomains = true
          this.setSnackbar({
            open: true,
            text: 'No Search Console properties found. Create or share a property in Google Search Console first.',
            type: 'info'
          })
        }
      } catch (error) {
        console.error('Error loading GSC data', error)
        const statusCode = error?.response?.status
        const message = this.getErrorMessage(error, 'Failed to load Google Search Console data')
        const errorMsg = (error?.response?.data?.message || error.message || '').toLowerCase()

        // Check for authentication/permission errors
        const isAuthError = statusCode === 401 || statusCode === 403 ||
          errorMsg.includes('no refresh token') ||
          errorMsg.includes('token') ||
          errorMsg.includes('auth') ||
          errorMsg.includes('permission') ||
          errorMsg.includes('insufficient')

        if (errorMsg.includes('no refresh token')) {
          this.gscState.noToken = true
        } else if (isAuthError) {
          this.gscState.noAccess = true
        } else {
          this.setSnackbar({
            open: true,
            text: `Error connecting to Google Search Console: ${message}`,
            type: 'error'
          })
        }
      } finally {
        this.isLoading.domains = false
      }
    },
    async connectSelectedGscProperty () {
      if (!this.gscProperty) {
        this.setSnackbar({ open: true, text: 'Please select a Search Console property to continue.', type: 'warning' })
        return
      }

      try {
        await this.withLoader('saving', async () => {
          const accessUser = this.domains?.find(domain => domain.domain === this.gscProperty)?.userId || null
          await this.postSearchConsoleIntegration({
            contentplanId: this.contentplanId,
            property: this.gscProperty,
            accessUser
          })

          this.setSnackbar({ open: true, text: 'Google Search Console connected successfully!', type: 'success' })
          await this.refreshIntegrations()
          this.navigateToStep(3)
        })
      } catch (error) {
        console.error('Failed to connect Google Search Console', error)
        const message = this.getErrorMessage(error, 'Failed to connect Google Search Console. Please try again or contact support if the issue persists.')
        this.setSnackbar({ open: true, text: message, type: 'error' })
      }
    },
    async handleConnectGscClick () {
      if (!this.contentplanId) return
      this.isLoading.connectGsc = true

      try {
        const targetRoute = this.$router.resolve({
          name: DEFAULT_STEP_ROUTE[2],
          params: { workspaceId: this.workspaceId, contentplanId: this.contentplanId }
        }).href
        const nextUrl = `${window.location.origin}${targetRoute}`

        const result = await this.updateWorkspace({
          workspaceId: this.workspaceId,
          action: 'generateAccessKey'
        })
        const accessKey = result?.accessKey
        if (!accessKey) throw new Error('Missing access key. Please try again.')

        const params = new URLSearchParams({
          next: nextUrl,
          contentplanId: String(this.contentplanId),
          scope: 'search-console',
          workspaceId: String(this.workspaceId),
          accessKey,
          prompt: 'select_account consent'
        })

        window.location.href = `${window.location.origin}/auth/in?${params.toString()}`
      } catch (error) {
        const message = this.getErrorMessage(error, 'Failed to initiate Google Search Console connection. Please try again.')
        this.setSnackbar({ open: true, text: message, type: 'error' })
        this.isLoading.connectGsc = false
      }
    },
    async saveWordpressApiKey () {
      if (!this.$refs.wordpressForm?.validate()) {
        this.setSnackbar({ open: true, text: 'Please enter the API key generated in the ContentGecko Connector plugin.', type: 'warning' })
        return
      }

      const apiKey = this.wordpressApiKey.trim()
      this.wordpressIntegrationSuccess = false

      try {
        await this.withLoader('connectWordpress', async () => {
          await this.postWordpressIntegration({
            contentplanId: this.contentplanId,
            type: 'wp-plugin',
            apiKey
          })

          await this.refreshIntegrations()
          this.wordpressApiKey = ''
          this.wordpressIntegrationSuccess = true
          this.setSnackbar({ open: true, text: 'WordPress plugin connected successfully!', type: 'success' })
        })
      } catch (error) {
        const message = this.getErrorMessage(error, 'Failed to save WordPress plugin integration. Please try again.')
        this.setSnackbar({ open: true, text: message, type: 'error' })
      }
    },
    async refreshIntegrations () {
      if (!this.contentplanId) return
      try {
        await this.getIntegrations({ contentplanId: this.contentplanId })
      } catch (error) {
        const message = this.getErrorMessage(error, 'Failed to load integration status')
        this.setSnackbar({ open: true, text: message, type: 'warning' })
      }
    },
    completeWordpressStep () {
      this.redirectToHomeWithMessage()
    },
    async handleConnectShopifyClick () {
      await this.startShopifyAuthInBackground()
    },
    completeShopifyStep () {
      sessionStorage.removeItem('shopify_pending_shop')
      sessionStorage.removeItem('shopify_install_onboarding')
      this.redirectToHomeWithMessage()
    },
    redirectToHomeWithMessage () {
      if (!this.contentplanId) return
      this.$router.push({
        name: 'Home',
        params: { workspaceId: this.workspaceId, contentplanId: this.contentplanId },
        query: { showSetupMessage: 'true' }
      })
    },
    async goToStep (targetStep) {
      if (targetStep < 1) return
      if (!this.isDefaultOnboarding && targetStep > 2) return
      if (targetStep > 1 && !this.contentplanId) {
        this.setSnackbar({ open: true, text: 'Please complete the first step before continuing.', type: 'info' })
        return
      }
      if (targetStep === this.step) {
        await this.handleStepSideEffects(targetStep)
        return
      }
      this.navigateToStep(targetStep)
    },
    getRouteNameForStep (targetStep) {
      if (this.isShopifyInstallMode) return SHOPIFY_STEP_ROUTE[targetStep]
      return DEFAULT_STEP_ROUTE[targetStep]
    },
    navigateToStep (targetStep, contentplanId = null) {
      const routeName = this.getRouteNameForStep(targetStep)
      if (!routeName) return

      const params = { workspaceId: this.workspaceId }
      const planId = contentplanId || this.contentplanId
      if (targetStep > 1) {
        if (!planId) return
        params.contentplanId = planId
      }

      if (this.$route.name === routeName && this.$route.params.contentplanId === params.contentplanId) {
        this.step = targetStep
        return
      }

      this.$router.push({ name: routeName, params })
    },
    async recoverFromError () {
      this.clearError()
      await this.bootstrap()
      this.setSnackbar({ open: true, text: 'Successfully recovered! Please continue with the onboarding.', type: 'success' })
    },
    normalizeWebsiteUrl (value) {
      let url = (value || '').trim()
      if (!url) return ''
      if (!/^https?:\/\//.test(url)) url = `https://${url}`
      return url
    },
    handleFatal (error, fallbackMessage) {
      const message = this.getErrorMessage(error, fallbackMessage)
      this.errorState = {
        hasError: true,
        message
      }
      this.setSnackbar({ open: true, text: message, type: 'error' })
    },
    clearError () {
      this.errorState = { hasError: false, message: '' }
    },
    enrichError (error, fallbackMessage) {
      const message = this.getErrorMessage(error, fallbackMessage)
      error.message = message
      return error
    },
    getErrorMessage (error, defaultMessage) {
      if (error?.response?.data?.message) return error.response.data.message
      if (error?.response?.data?.error) return error.response.data.error
      if (error?.message) return error.message
      if (typeof error === 'string') return error
      return defaultMessage
    },
    async withLoader (key, task) {
      if (!Object.prototype.hasOwnProperty.call(this.isLoading, key)) {
        throw new Error(`Unknown loader key: ${key}`)
      }
      if (this.isLoading[key]) return
      this.isLoading[key] = true
      try {
        return await task()
      } finally {
        this.isLoading[key] = false
      }
    }
  }
}
